import React from "react";
import { clinicBasePrice, suggestedConnectionPack } from "@/domain/projection/engine";
import { formatArs, formatNumber, newProjectionId } from "@/domain/projection/format";
import type { Clinic, ProjectionModel } from "@/domain/projection/types";
import { Button } from "@/presentation/components/common";
import { ProjectionNumberField, projectionFieldClass } from "./ProjectionNumberField";

type PortfolioEditorProps = {
  model: ProjectionModel;
  onChange: (model: ProjectionModel) => void;
  liveClinicCount?: number | null;
};

export const PortfolioEditor: React.FC<PortfolioEditorProps> = ({
  model,
  onChange,
  liveClinicCount,
}) => {
  const updateClinic = (id: string, patch: Partial<Clinic>) => {
    onChange({
      ...model,
      clinics: model.clinics.map((clinic) => (clinic.id === id ? { ...clinic, ...patch } : clinic)),
    });
  };

  const enabled = model.clinics.filter((clinic) => clinic.enabled);

  return (
    <div className="space-y-4">
      <div className="panel-card p-4">
        <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100">Modo de cartera</h3>
        <p className="mt-1 text-xs text-slate-500">
          Detallada usa cada clínica (escala de profesionales y consultorios). Sintética proyecta con un promedio.
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          <Button
            size="sm"
            variant={model.portfolioMode === "detailed" ? "primary" : "secondary"}
            onClick={() => onChange({ ...model, portfolioMode: "detailed" })}
          >
            Cartera detallada
          </Button>
          <Button
            size="sm"
            variant={model.portfolioMode === "synthetic" ? "primary" : "secondary"}
            onClick={() => onChange({ ...model, portfolioMode: "synthetic" })}
          >
            Cartera sintética
          </Button>
          {typeof liveClinicCount === "number" ? (
            <Button
              size="sm"
              variant="secondary"
              onClick={() =>
                onChange({
                  ...model,
                  portfolioMode: "synthetic",
                  synthetic: { ...model.synthetic, clinicCount: liveClinicCount },
                })
              }
            >
              Usar {liveClinicCount} clínicas activas del panel
            </Button>
          ) : null}
        </div>
        {model.portfolioMode === "synthetic" ? (
          <div className="mt-4 grid gap-3 sm:grid-cols-3">
            <ProjectionNumberField
              label="Cantidad de clínicas"
              value={model.synthetic.clinicCount}
              onChange={(clinicCount) => onChange({ ...model, synthetic: { ...model.synthetic, clinicCount } })}
              min={0}
              step={1}
            />
            <ProjectionNumberField
              label="Profesionales promedio"
              value={model.synthetic.avgProfessionals}
              onChange={(avgProfessionals) =>
                onChange({ ...model, synthetic: { ...model.synthetic, avgProfessionals } })
              }
              min={1}
              step={0.1}
            />
            <ProjectionNumberField
              label="Consultorios promedio"
              value={model.synthetic.avgConsultorios}
              onChange={(avgConsultorios) =>
                onChange({ ...model, synthetic: { ...model.synthetic, avgConsultorios } })
              }
              min={1}
              step={0.1}
            />
          </div>
        ) : (
          <p className="mt-3 text-xs text-slate-500">
            {enabled.length} clínicas activas en la simulación · ticket base+escala promedio{" "}
            {formatArs(
              enabled.length > 0
                ? enabled.reduce((sum, clinic) => sum + clinicBasePrice(clinic, model), 0) / enabled.length
                : 0,
            )}
          </p>
        )}
      </div>

      <div className="panel-table-wrap">
        <div className="flex items-center justify-between gap-3 border-b border-slate-100 px-4 py-3 dark:border-gray-700">
          <div>
            <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100">Clínicas</h3>
            <p className="text-xs text-slate-500">
              WA hint es el volumen de referencia (último mes del Excel) para sugerir cupo de Conexión.
            </p>
          </div>
          <Button
            size="sm"
            onClick={() =>
              onChange({
                ...model,
                clinics: [
                  ...model.clinics,
                  {
                    id: newProjectionId("clinic"),
                    name: `Clínica ${String(model.clinics.length + 1).padStart(2, "0")}`,
                    professionals: 1,
                    consultorios: 1,
                    enabled: true,
                    whatsappHint: 0,
                  },
                ],
              })
            }
          >
            Agregar clínica
          </Button>
        </div>
        <div className="panel-table-scroll-hint">Deslizá horizontalmente para ver todas las columnas.</div>
        <table className="min-w-full text-left text-sm">
          <thead className="panel-thead text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-3 py-2">Activa</th>
              <th className="px-3 py-2">Nombre</th>
              <th className="px-3 py-2">Prof.</th>
              <th className="px-3 py-2">Consult.</th>
              <th className="px-3 py-2">WA hint</th>
              <th className="px-3 py-2">Cupo sugerido</th>
              <th className="px-3 py-2 text-right">Base + escala</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {model.clinics.map((clinic) => {
              const suggested = suggestedConnectionPack(clinic.whatsappHint, model.connectionPacks);
              return (
                <tr key={clinic.id} className="border-t border-slate-100 dark:border-gray-700">
                  <td className="px-3 py-2">
                    <input
                      type="checkbox"
                      checked={clinic.enabled}
                      onChange={(event) => updateClinic(clinic.id, { enabled: event.target.checked })}
                    />
                  </td>
                  <td className="px-3 py-2 min-w-[10rem]">
                    <input
                      className={projectionFieldClass}
                      value={clinic.name}
                      onChange={(event) => updateClinic(clinic.id, { name: event.target.value })}
                    />
                  </td>
                  <td className="px-3 py-2 w-24">
                    <input
                      type="number"
                      className={projectionFieldClass}
                      min={1}
                      value={clinic.professionals}
                      onChange={(event) =>
                        updateClinic(clinic.id, { professionals: Math.max(1, Number(event.target.value) || 1) })
                      }
                    />
                  </td>
                  <td className="px-3 py-2 w-24">
                    <input
                      type="number"
                      className={projectionFieldClass}
                      min={1}
                      value={clinic.consultorios}
                      onChange={(event) =>
                        updateClinic(clinic.id, { consultorios: Math.max(1, Number(event.target.value) || 1) })
                      }
                    />
                  </td>
                  <td className="px-3 py-2 w-28">
                    <input
                      type="number"
                      className={projectionFieldClass}
                      min={0}
                      value={clinic.whatsappHint}
                      onChange={(event) =>
                        updateClinic(clinic.id, { whatsappHint: Math.max(0, Number(event.target.value) || 0) })
                      }
                    />
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-500">
                    {suggested ? `${formatNumber(suggested.quota)} · ${formatArs(suggested.price)}` : "—"}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{formatArs(clinicBasePrice(clinic, model))}</td>
                  <td className="px-3 py-2">
                    <button
                      type="button"
                      className="text-xs font-medium text-rose-600"
                      onClick={() =>
                        onChange({ ...model, clinics: model.clinics.filter((row) => row.id !== clinic.id) })
                      }
                    >
                      Quitar
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};
